// pages/EncuestasCuestionario.js
import React, { useState, useEffect } from 'react';
import {
  Select, Form, Input, InputNumber, DatePicker, Switch, Radio, Space,
  Button, Typography, message, Row, Col, Card, Spin, Result, Alert
} from 'antd';
import {
  SaveOutlined, ReloadOutlined, UserOutlined, HomeOutlined,
  GlobalOutlined, CommentOutlined, SmileOutlined,
  FrownOutlined, MehOutlined, StarOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Text } = Typography;

const VALORACIONES = [
  { value: 'EXCELENTE', label: 'Excelente', color: '#52c41a', icon: <SmileOutlined /> },
  { value: 'BIEN',      label: 'Bien',      color: '#1890ff', icon: <SmileOutlined /> },
  { value: 'REGULAR',   label: 'Regular',   color: '#faad14', icon: <MehOutlined /> },
  { value: 'MAL',       label: 'Mal',       color: '#ff4d4f', icon: <FrownOutlined /> },
];

const EncuestasCuestionario = () => {
  const [form] = Form.useForm();
  const [cuestionario, setCuestionario] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [erroresValidacion, setErroresValidacion] = useState([]);

  const [paises, setPaises] = useState([]);  

  const renderFlag = (codigoISO) => {
    if (!codigoISO || codigoISO.trim() === '') {
      return <span style={{ marginRight: '8px' }}>🌍</span>;
    }
    return <span className={`fi fi-${codigoISO.toLowerCase()}`} style={{ marginRight: '8px' }} />;
  };


  useEffect(() => { fetchCuestionario(); }, []);

  const fetchCuestionario = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/encuestas/cuestionario`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCuestionario(response.data.data || []);

      const responsePaises = await axios.get(`${BASE_URL}/api/paises`, {
        headers: { Authorization: `Bearer ${token}` }
      });      

      setPaises(responsePaises.data.data)      
    } catch (error) {
      console.error('❌ Error cargando cuestionario:', error);
      message.error('Error al cargar el cuestionario');
    } finally {
      setLoading(false);
    }
  };

  // Validación manual: devuelve lista de campos faltantes con su descripción
  const validarFormulario = (values) => {
    const faltantes = [];

    cuestionario.forEach(cat => {
      cat.Preguntas.forEach(p => {
        const valoracion = values[`pregunta_${p.Id}`];
        if (!valoracion) {
          faltantes.push({
            campo: `pregunta_${p.Id}`,
            descripcion: `[${cat.Descripcion}] ${p.Texto}`,
          });
        }
      });
    });

    return faltantes;
  };

  const handleSubmit = async (values) => {
    // 1) Validación previa personalizada
    const faltantes = validarFormulario(values);

    if (faltantes.length > 0) {
      setErroresValidacion(faltantes);
      message.warning(`Faltan ${faltantes.length} pregunta(s) por valorar`);

      // Scroll al primer campo faltante
      const primerCampo = faltantes[0].campo;
      form.scrollToField(primerCampo, { behavior: 'smooth', block: 'center' });

      // Marcar los campos con error
      form.setFields(
        faltantes.map(f => ({
          name: f.campo,
          errors: ['Seleccione una valoración'],
        }))
      );
      return;
    }

    setErroresValidacion([]);

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      // Construir array de respuestas
      const respuestas = [];
      cuestionario.forEach(cat => {
        cat.Preguntas.forEach(p => {
          const valoracion = values[`pregunta_${p.Id}`];
          const comentario = values[`comentario_${p.Id}`];
          if (valoracion) {
            respuestas.push({
              IdPregunta: p.Id,
              CodigoCategoria: cat.Codigo,
              Valoracion: valoracion,
              Comentario: comentario || null
            });
          }
        });
      });

      const payload = {
        PaisProcedencia: values.PaisProcedencia,
        NumHabitacion: values.NumHabitacion,
        Edad: values.Edad,
        FechaEntrada: values.FechaEntrada ? values.FechaEntrada.format('YYYY-MM-DD') : null,
        FechaSalida: values.FechaSalida ? values.FechaSalida.format('YYYY-MM-DD') : null,
        HaEstadoAntes: values.HaEstadoAntes || false,
        LeGustariaVolver: values.LeGustariaVolver || false,
        Observaciones: values.Observaciones,
        Respuestas: respuestas
      };

      await axios.post(`${BASE_URL}/api/encuestas/respuestas`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('Encuesta guardada correctamente');
      setSaved(true);
    } catch (error) {
      console.error('❌ Error guardando encuesta:', error);
      message.error(error.response?.data?.error || 'Error al guardar la encuesta');
    } finally {
      setSaving(false);
    }
  };

  // Al fallar validación nativa de antd (por si acaso)
  const handleFinishFailed = ({ errorFields }) => {
    const faltantes = errorFields.map(f => ({
      campo: f.name.join('.'),
      descripcion: f.errors?.[0] || 'Campo requerido'
    }));
    setErroresValidacion(faltantes);
    message.warning(`Faltan ${errorFields.length} campo(s) por rellenar`);
  };

  const handleReset = () => {
    form.resetFields();
    setSaved(false);
    setErroresValidacion([]);
  };

  if (saved) {
    return (
      <PageContainer title="Encuesta guardada" icon={<StarOutlined />}>
        <Result
          status="success"
          title="¡Encuesta registrada correctamente!"
          subTitle="Gracias por su tiempo. La encuesta ha sido guardada."
          extra={[
            <Button type="primary" key="new" onClick={handleReset}>
              Nueva encuesta
            </Button>,
          ]}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Cuestionario de Encuesta"
      subtitle="Rellene los datos del cliente y valore cada aspecto"
      icon={<StarOutlined />}
      loading={loading}
      onRefresh={fetchCuestionario}
      cardStyle={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={handleFinishFailed}
          scrollToFirstError
        >
          {/* AVISO DE CAMPOS FALTANTES */}
          {erroresValidacion.length > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              style={{ marginBottom: 16, borderRadius: 8 }}
              message={`Faltan ${erroresValidacion.length} campo(s) por completar`}
              description={
                <ul style={{ margin: 0, paddingLeft: 20, maxHeight: 200, overflowY: 'auto' }}>
                  {erroresValidacion.map((e, i) => (
                    <li key={i} style={{ fontSize: 12 }}>
                      <a
                        onClick={() => form.scrollToField(e.campo, { behavior: 'smooth', block: 'center' })}
                        style={{ color: '#d46b08' }}
                      >
                        {e.descripcion}
                      </a>
                    </li>
                  ))}
                </ul>
              }
              closable
              onClose={() => setErroresValidacion([])}
            />
          )}

          {/* DATOS DEL CLIENTE */}
          <Card
            title={<><UserOutlined /> Datos del cliente</>}
            style={{ marginBottom: 16, borderRadius: 8 }}
            size="small"
          >
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="PaisProcedencia" label="País de procedencia">
                  {/* <Input placeholder="Ej: España" prefix={<GlobalOutlined />} maxLength={50} /> */}
                  <Select
                    showSearch
                    placeholder="Seleccione pais"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {paises.map(pais => (
                      <Option key={pais.Codigo} value={pais.Descripcion} label={pais.Descripcion}>
                        {renderFlag(pais.CodigoISO)}
                        {pais.Descripcion}
                      </Option>
                    ))}
                  </Select>                  
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="NumHabitacion" label="Nº de habitación">
                  <Input placeholder="Ej: 204" prefix={<HomeOutlined />} maxLength={20} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="Edad" label="Edad">
                  <InputNumber style={{ width: '100%' }} min={0} max={120} placeholder="Ej: 35" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="FechaEntrada" label="Fecha de llegada">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Seleccione fecha" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="FechaSalida" label="Fecha de salida">
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Seleccione fecha" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="HaEstadoAntes"
                  label="¿Ha estado antes en el hotel?"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Sí" unCheckedChildren="No" style={{ minWidth: 80 }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* PREGUNTAS POR CATEGORÍA - Layout horizontal compacto */}
          {cuestionario.map(categoria => (
            <Card
              key={categoria.Codigo}
              title={<><StarOutlined /> {categoria.Descripcion}</>}
              style={{ marginBottom: 16, borderRadius: 8 }}
              size="small"
            >
              {categoria.Preguntas.length === 0 ? (
                <Text type="secondary">No hay preguntas en esta categoría</Text>
              ) : (
                categoria.Preguntas.map((pregunta, idx) => (
                  <div
                    key={pregunta.Id}
                    style={{
                      marginBottom: 12,
                      paddingBottom: 12,
                      borderBottom: idx < categoria.Preguntas.length - 1
                        ? '1px dashed #f0f0f0' : 'none'
                    }}
                  >
                    <Row gutter={16} align="middle" wrap={false}>
                      {/* Texto de la pregunta a la IZQUIERDA */}
                      <Col flex="auto" style={{ minWidth: 0 }}>
                        <Text strong style={{ fontSize: 13 }}>{pregunta.Texto}</Text>
                      </Col>

                      {/* Opciones a la DERECHA */}
                      <Col flex="none">
                        <Form.Item
                          name={`pregunta_${pregunta.Id}`}
                          style={{ marginBottom: 0 }}
                          rules={[{ required: true, message: 'Seleccione una valoración' }]}
                        >
                          <Radio.Group size="middle">
                            <Space size="middle">
                              {VALORACIONES.map(v => (
                                <Radio key={v.value} value={v.value}>
                                  <span style={{ color: v.color, fontWeight: 500, fontSize: 12 }}>
                                    {v.icon} {v.label}
                                  </span>
                                </Radio>
                              ))}
                            </Space>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Comentario opcional debajo, solo si la pregunta lo permite */}
                    {pregunta.PermiteTexto && (
                      <Form.Item
                        name={`comentario_${pregunta.Id}`}
                        style={{ marginTop: 6, marginBottom: 0 }}
                      >
                        <Input
                          placeholder="Comentario (opcional)"
                          maxLength={500}
                          prefix={<CommentOutlined style={{ color: '#bbb' }} />}
                        />
                      </Form.Item>
                    )}
                  </div>
                ))
              )}
            </Card>
          ))}

          {/* PREGUNTAS GENERALES */}
          <Card
            title={<><CommentOutlined /> Valoración general</>}
            style={{ marginBottom: 16, borderRadius: 8 }}
            size="small"
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="LeGustariaVolver"
                  label="¿Le gustaría volver a alojarse con nosotros?"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Sí" unCheckedChildren="No" style={{ minWidth: 80 }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="Observaciones" label="Observaciones generales del hotel">
              <Input.TextArea
                rows={4}
                maxLength={1000}
                showCount
                placeholder="Escriba aquí cualquier comentario, sugerencia o queja..."
              />
            </Form.Item>
          </Card>

          {/* BOTONES */}
          <div style={{ textAlign: 'right', marginTop: 16 }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Limpiar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                Guardar encuesta
              </Button>
            </Space>
          </div>
        </Form>
      </Spin>
    </PageContainer>
  );
};

export default EncuestasCuestionario;